import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  areaAuditEvidenceSummary,
  areaAuditKindHint,
  areaAuditKindLabel,
} from "@/lib/tina/area-audit";
import type { AreaAuditFindingRow } from "@/types/tina";

interface AreaDismissFindingDialogProps {
  /** The finding to dismiss; `null` closes the dialog. */
  finding: AreaAuditFindingRow | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (note: string) => void;
}

/** Who and where a finding is about: `venue · area · city`, whichever it names. */
export const areaAuditSubject = (finding: AreaAuditFindingRow): string =>
  [
    finding.venue_name || finding.venue_id,
    finding.area_name || finding.area_id,
    [finding.city, finding.country_code].filter(Boolean).join(", "),
  ]
    .filter((part): part is string => Boolean(part))
    .join("  ·  ");

/**
 * The one confirmation on the Area tab, because a dismissal is permanent: the finding
 * stays dismissed until the nightly area step sees different evidence for it. It
 * re-states the finding rather than asking about an id, and the optional note is the only
 * record of why a curator accepted it.
 */
export function AreaDismissFindingDialog({
  finding,
  pending,
  onOpenChange,
  onConfirm,
}: AreaDismissFindingDialogProps) {
  const [note, setNote] = useState("");

  // A fresh note per finding: carrying one over would attribute one curator's reasoning
  // to a different area.
  useEffect(() => {
    setNote("");
  }, [finding?.id]);

  const hint = finding ? areaAuditKindHint(finding.kind) : undefined;
  const summary = finding ? areaAuditEvidenceSummary(finding) : "";

  return (
    <Dialog open={finding !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dismiss this finding?</DialogTitle>
          <DialogDescription>
            It will not appear again unless its evidence changes. There is no
            undo on this page.
          </DialogDescription>
        </DialogHeader>

        {finding ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  finding.severity === "high" ? "destructive" : "secondary"
                }
                className="text-[10px] uppercase"
              >
                {finding.severity}
              </Badge>
              <Badge variant="outline" className="text-[10px] font-normal">
                {areaAuditKindLabel(finding.kind)}
              </Badge>
            </div>
            <p className="text-sm font-medium">{areaAuditSubject(finding)}</p>
            {summary ? <p className="text-sm">{summary}</p> : null}
            {hint ? (
              <p className="text-xs text-muted-foreground">{hint}</p>
            ) : null}
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why is this acceptable? Optional, but the next curator reads it."
              className="min-h-[80px] text-sm"
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Keep it
          </Button>
          <Button onClick={() => onConfirm(note)} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Dismiss for good
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
